/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useRef, useEffect, useState } from 'react';
import { StepsProvider, useStepsContext } from '@/shared/ui';
import { useTranslation } from 'react-i18next';
import { PageSEO } from '@/shared/seo';
import { onboardingFormContext, onboardingInitialValues } from '../context/OnboardingContext';
import PlanChoice from '../components/sections/PlanChoice';
import Checkout from '../components/sections/Checkout';
import Activation from '../components/sections/Activation';
import { usePersistedOnboarding } from '../hooks/usePersistedOnboarding';

const WIZARD_ID = 'onboarding';
/**
 * Three steps, and the first one is the plan.
 *
 * The wizard used to open on "what are you here to do?", then a form, and only
 * then the plans — two screens of answers before the customer saw a price. The
 * segment moved onto the plan screen's segmented control and the form moved
 * into the checkout, where it asks only for what Payien needs to register the
 * subscription and issue the invoice.
 *
 * `GuidedActionChoice`, `AgencyData` and `SoloBrokerData` are still in the
 * tree, unrouted. Note that the removed first step was also the only entry
 * point for someone holding an invitation to an existing agency: that path has
 * no home yet and is tracked as an open gap.
 */
const STEP_ORDER = ['plan', 'checkout', 'activation'];

/**
 * The onboarding wizard page: wires steps, form context, and SEO.
 *
 * @returns The onboarding page element.
 */
const OnboardingPage: React.FC = () => {
  const { t } = useTranslation('@pages/onboarding');

  return (
    <StepsProvider
      initialStepIdOrder={STEP_ORDER}
      wizardId={WIZARD_ID}
      enableUrlNavigation={true}
      stepParamName="step"
    >
      <PageSEO title={t('pageTitle')} description="" noIndex />
      <onboardingFormContext.Provider initialState={{ values: onboardingInitialValues }}>
        <OnboardingBody />
      </onboardingFormContext.Provider>
    </StepsProvider>
  );
};

const FADE_DURATION = 280;

/**
 * The onboarding wizard body: renders the active step with fade transitions.
 *
 * @returns The onboarding body element.
 */
const OnboardingBody: React.FC = () => {
  const { useStore } = onboardingFormContext;
  const values = useStore((state: any) => state.values);
  const { currentStepId } = useStepsContext();
  const updateValues = useStore((state: any) => state.updateValues);
  const seededRef = useRef(false);

  usePersistedOnboarding();

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const urlAgencyId = params.get('agency_id');
    const urlPlanId = params.get('plan_id');
    const patch: Record<string, unknown> = {};
    if (urlAgencyId && !values.agencyId) patch.agencyId = urlAgencyId;
    if (urlPlanId && !values.selectedPlanId) patch.selectedPlanId = urlPlanId;
    if (Object.keys(patch).length > 0) updateValues(patch);
  }, [values.agencyId, values.selectedPlanId, updateValues]);

  const isSoloBroker = values.agencyType === 'solo_broker';

  const prevStepRef = useRef(currentStepId);
  const [fadeClass, setFadeClass] = useState('onb-fade-in');
  const [renderedStepId, setRenderedStepId] = useState(currentStepId);

  useEffect(() => {
    if (prevStepRef.current !== currentStepId) {
      setFadeClass('onb-fade-out');
      const timer = setTimeout(() => {
        prevStepRef.current = currentStepId;
        setRenderedStepId(currentStepId);
        setFadeClass('onb-fade-in');
      }, FADE_DURATION);
      return () => clearTimeout(timer);
    }
  }, [currentStepId]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: 'var(--surface-primary)',
        fontFamily: 'var(--font-family)',
      }}
    >
      <style>{`
        .onb-fade-in { opacity: 1; transition: opacity ${FADE_DURATION}ms ease; }
        .onb-fade-out { opacity: 0; transition: opacity ${FADE_DURATION / 2}ms ease; }
      `}</style>

      {/* Step content — overflowY:auto allows scrolling when viewport is small (zoom) */}
      <div
        className={`onb-agency-form ${fadeClass}`}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', minHeight: 0, position: 'relative', willChange: 'opacity' }}
      >
        {renderedStepId === 'plan' && <PlanChoice />}

        {renderedStepId === 'checkout' && (
          <Checkout
            planId={values.selectedPlanId}
            planName={values.selectedPlanName}
            planKey={values.selectedPlanKey}
            billingFrequency={values.selectedBillingFrequency}
            seatPrice={values.selectedPlanPrice}
            segment={values.selectedSegment}
            agencyId={values.agencyId}
            agencyPayerKey={values.agencyPayerKey}
            agencyName={values.agencyName}
          />
        )}

        {renderedStepId === 'activation' && (
          <Activation
            agencyId={values.agencyId}
            agencyName={values.agencyName}
            planName={values.selectedPlanName}
            planId={values.selectedPlanId}
            isSoloBroker={isSoloBroker}
            seats={values.checkoutSeats}
            lines={values.checkoutLines}
            supportSeats={values.checkoutSupportSeats}
            totalPaid={values.checkoutTotal}
            billingCycle={values.checkoutCycle}
            trialDays={values.checkoutTrialDays}
          />
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
